import React, {PureComponent} from 'react';
import ReactMarkdown from 'react-markdown';

import {ImageresizeApi} from 'modules';
import {BannerAd, BulkList, Button, ImagePicker} from 'components';
import {GENERIC_UPLOAD_ERROR, MAX_UPLOAD_COUNT} from 'constants/images';
import {getId, getUploadedImageErrorMessage, getUrlImageType, throttle} from 'utils';

import bulkResizeHeader from './bulk-resize-header.md';
import whatIsBatchResize from './what-is-batch-resize.md';
import batchImageResize from './batch-image-resize.md';
import bulkImageCompressor from './bulk-image-compressor.md';
import whatIsDifferent from './what-is-different.md';
import bulkImageConverter from './bulk-image-converter.md';

export default class BulkImageSelect extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      bulkImages: [],
      urlValues: {},
      maxLimitReached: false
    };
  }

  handleDropImages = (acceptedFiles, rejectedFiles) => {
    const {onErrors} = this.props;
    const {urlValues} = this.state;

    const urlCount = Object.values(urlValues).filter((val) => !!val).length;

    let currentFiles = this.state.bulkImages.length + urlCount; 
    if(currentFiles >= MAX_UPLOAD_COUNT) {
      return;
    }
    // The upload failed, tell our parent
    if (rejectedFiles.length) {
      return onErrors(rejectedFiles.map(getUploadedImageErrorMessage));
    }
    let limitReached = this.state.maxLimitReached;
    if(acceptedFiles.length > MAX_UPLOAD_COUNT - currentFiles ) {
      limitReached = true;
      acceptedFiles = acceptedFiles.slice(0, MAX_UPLOAD_COUNT - currentFiles);
    }

    // Format the files in a way that's useful for the rest of the app and API
    const images = acceptedFiles.map((file) => ({
      id: getId(),
      contentLength: file.size,
      name: file.name,
      state: 'ready',
      uploadType: 'image',
      uploadProgress: -1,
      value: file
    }));

    // Store the successful uploads
    this.setState(({ bulkImages }) => ({
      maxLimitReached: limitReached,
      bulkImages: [ ...bulkImages, ...images ]
    }));
  };

  handleChangeUrl = (name, value) => this.setState(({ urlValues }) => ({
    urlValues: {
      ...urlValues,
      [name]: value
    }
  }));

  handleRemoveImage = (id) => this.setState(({ bulkImages }) => ({
    bulkImages: bulkImages.filter((image) => image.id !== id)
  }));

  handleProceedToBulkResize = () => {
    const {urlValues} = this.state;
    const {onDone, onErrors} = this.props;
    const urls = Object.values(urlValues).map((value) => ({
      id: getId(),
      name: value,
      state: 'ready',
      uploadType: 'url',
      uploadProgress: -1,
      value
    }));
    const bulkImages = [ ...this.state.bulkImages, ...urls ];
    let inFlightUploads = 0;

    // Set up the final list including urls and uploaded images, also cleanup urls
    this.setState({
      urlValues: {},
      bulkImages
    });

    // When an upload is successful, call onDone as soon as we can
    const uploadSuccessful = () => {
      inFlightUploads -= 1;

      if (inFlightUploads === 0) {
        onDone(this.state.bulkImages);
      }
    };

    // As each upload finishes, update the app state. Once all uploads are complete, tell our parents.
    const uploadDone = (err, data) => {

      if (err) {
        return onErrors([err]);
      }

      const { image, result } = data;

      // The image has already been uploaded
      if (image.imageId) {
        return uploadSuccessful();
      }

      this.setState(
        ({ bulkImages }) => {
          const newImage = {
            ...image,
            state: 'uploaded',
            uploadProgress: 1,
            contentLength: result.contentLength,
            dimensions: result.dimensions,
            imageId: result.imageId,
            preview: ImageresizeApi.getImageUrl(result.imageId),
            type: image.uploadType === 'image' ?
              image.value.type.replace('image/', '') :
              getUrlImageType(image.value),
            name: image.uploadType === 'image' ?
              image.value.name :
              image.value
          };

          return {
            bulkImages: bulkImages.map((item) => (item.id === newImage.id) ? newImage : item)
          };
        },
        () => {
          uploadSuccessful();
        }
      );
    };

    // Update the progress of each image that's being uploaded, batch state updates so the UI doesn't get overloaded
    const updateProgress = ((self) => {
      const status = {};
      const updateState = throttle(() => self.setState(({ bulkImages }) => ({
        bulkImages: bulkImages.map((item) => {

          // Only update progress if this value is larger to avoid race conditions
          if (status[item.id] && status[item.id] > item.uploadProgress) {
            return {
              ...item,
              state: 'uploading',
              uploadProgress: status[item.id]
            };
          }

          return item;
        })
      })), 50);

      return (imageId, percentComplete) => {
        status[imageId] = percentComplete;

        updateState();
      };
    })(this);

    // Trigger the upload
    bulkImages.forEach((image) => {
      inFlightUploads += 1;

      // If the image is somehow already uploaded, don't reupload it.
      if (image.imageId) {
        return uploadDone(null, { image });
      }

      const upload = ImageresizeApi.postImages([image]);

      upload.on('error', (ev) => uploadDone(new Error(GENERIC_UPLOAD_ERROR)));
      upload.on('progress', ({ percentComplete }) => updateProgress(image.id, percentComplete));
      upload.on('load', () => {

        if (
          upload.xhr.status !== 200 ||
          !upload.response.results ||
          !upload.response.results.length ||
          upload.response.results.find((result) => result.state === 'error')
        ) {
          return uploadDone(new Error(GENERIC_UPLOAD_ERROR));
        }

        const result = upload.response.results[0];

        uploadDone(null, { result, image });
      });
    });
  }

  isUploadInProgress = () => {
    const {bulkImages} = this.state;

    return !!bulkImages.find((image) => image.state !== 'ready');
  };

  shouldPickerBeDisabled() {
    const {bulkImages, urlValues} = this.state;
    const urlCount = Object.values(urlValues).filter((val) => !!val).length;

    return !!(this.isUploadInProgress() || (urlCount + bulkImages.length) >= MAX_UPLOAD_COUNT);
  }

  shouldButtonBeActive() {
    const {bulkImages, urlValues} = this.state;
    const urlsPopulated = Object.values(urlValues).find((val) => !!val);
    const uploadsInProgress = bulkImages.find((val) => Math.abs(val.uploadProgress) !== 1);

    return !!((bulkImages.length && !uploadsInProgress) || urlsPopulated);
  }

  renderList() {
    const {bulkImages} = this.state;

    if (!bulkImages.length) {
      return null;
    }

    return (
      <BulkList
        images={bulkImages}
        removeImage={this.handleRemoveImage}
      />
    );
  }

  renderButton() {
    return (
      <div className="selection-box">
        <Button
          className='main-button'
          disabled={!this.shouldButtonBeActive()}
          onClick={this.handleProceedToBulkResize}
        >
          Proceed to Bulk Resize
        </Button>
      </div>
    );
  }
  clearLimitReachMsg = () => this.setState({maxLimitReached: false});

  render() {
    const { maxLimitReached } = this.state;
    return (
      <div className='container'>
        <ReactMarkdown className={'center section'} source={bulkResizeHeader} />
        <div className='selection-box'>
          <ImagePicker
            accept='image/*'
            fileType="Image"
            disabled={this.shouldPickerBeDisabled()}
            uploading={this.isUploadInProgress()}
            dropImages={this.handleDropImages}
            changeUrl={this.handleChangeUrl}
            submitUrlUpload={this.handleProceedToBulkResize}
            multiple={true}
            maxLimitReached={maxLimitReached}
            clearLimitReachMsg={this.clearLimitReachMsg}
          />
        </div>
        {this.renderList()}
        {this.renderButton()}
        <BannerAd />
        <div className='content'>
          <ReactMarkdown className='section' source={whatIsBatchResize} />
          <div className='twoSections'>
            <ReactMarkdown className='section' source={batchImageResize} />
            <ReactMarkdown className='section' source={bulkImageCompressor} />
          </div>
          <div className='twoSections'>
            <ReactMarkdown className='section' source={whatIsDifferent} />
            <ReactMarkdown className='section' source={bulkImageConverter} />
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/BulkResize/BulkImageSelect.jsx